/**
 * sessionStorage key holding the epoch milliseconds of the last recovery reload.
 */
const RELOAD_MARKER_KEY: string = 'd-shell-app-stale-chunk-reload';

/**
 * Minimum gap between recovery reloads, so a chunk that is genuinely missing
 * cannot put the tab into a reload loop.
 */
const RELOAD_COOLDOWN_MS: number = 60000;

/**
 * Lower-cased message fragments browsers use when a dynamic import cannot be loaded.
 * A chunk that 404s rejects with the "fetch" wording; a chunk answered by an HTML
 * error page rejects with the module script or MIME wording. Chrome, Firefox and
 * Safari each word these differently.
 */
const STALE_CHUNK_MESSAGES: ReadonlyArray<string> = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
  'expected a javascript module script',
  'unable to preload css',
];

/**
 * Determines whether an error is a chunk that could not be loaded rather than an
 * error thrown by application code.
 *
 * @param error Rejection value from a dynamic import or a router navigation.
 * @returns True when the error matches a browser dynamic-import failure.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message: string = (error instanceof Error ? error.message : String(error ?? '')).toLowerCase();

  return STALE_CHUNK_MESSAGES.some((fragment: string): boolean => message.includes(fragment));
}

/**
 * Reloads the page when a chunk request fails because the site was redeployed while
 * this tab was open: the loaded JavaScript asks for file names that the Static Web App
 * no longer serves, and a reload fetches the current `index.html` and chunk names.
 *
 * @param error Rejection value from a dynamic import or a router navigation.
 * @returns True when a reload was triggered, so the caller can skip its own handling.
 */
export function reloadOnStaleChunkError(error: unknown): boolean {
  if (!isStaleChunkError(error)) {
    return false;
  }

  // A dropped connection fails the same way; reloading offline would only replace the
  // app with the browser's network error page.
  if (!navigator.onLine) {
    return false;
  }

  const now: number = Date.now();

  try {
    const lastReload: number = Number(sessionStorage.getItem(RELOAD_MARKER_KEY) ?? 0);

    if (Number.isFinite(lastReload) && now - lastReload < RELOAD_COOLDOWN_MS) {
      return false;
    }

    sessionStorage.setItem(RELOAD_MARKER_KEY, String(now));
  } catch {
    // Private browsing modes can block sessionStorage; recovering once is still worthwhile.
  }

  window.location.reload();

  return true;
}
